/**
 * État global du mode personnalisation du dashboard.
 * Persisté en sessionStorage pour conserver le mode édition lors des navigations
 * (ex. aller configurer un widget puis revenir sur la grille).
 */
import {
  createContext,
  useContext,
  useState,
  type PropsWithChildren,
} from "react";

type DashboardEditContextValue = {
  isEditing: boolean;
  setIsEditing: (value: boolean) => void;
  enterEditMode: () => void;
  exitEditMode: () => void;
};

const STORAGE_KEY = "dashboard-edit-mode";

const DashboardEditContext = createContext<DashboardEditContextValue | null>(
  null
);

// En mémoire : remis à false à chaque (re)chargement de page, conservé pendant
// toute la session JS (donc à travers les navigations internes du SPA).
let hasBootedThisPageLoad = false;

function readInitialEditMode() {
  if (!hasBootedThisPageLoad) {
    // Ouverture / rechargement de la page → édition fermée par défaut.
    hasBootedThisPageLoad = true;
    sessionStorage.removeItem(STORAGE_KEY);
    return false;
  }
  // Navigation interne (ex. aller au configurateur puis revenir) → on restaure.
  return sessionStorage.getItem(STORAGE_KEY) === "true";
}

export function DashboardEditProvider({ children }: PropsWithChildren) {
  const [isEditing, setIsEditingState] = useState(readInitialEditMode);

  const setIsEditing = (value: boolean) => {
    setIsEditingState(value);
    sessionStorage.setItem(STORAGE_KEY, String(value));
  };

  return (
    <DashboardEditContext.Provider
      value={{
        isEditing,
        setIsEditing,
        enterEditMode: () => setIsEditing(true),
        exitEditMode: () => setIsEditing(false),
      }}
    >
      {children}
    </DashboardEditContext.Provider>
  );
}

const defaultEditContext: DashboardEditContextValue = {
  isEditing: false,
  setIsEditing: () => undefined,
  enterEditMode: () => undefined,
  exitEditMode: () => undefined,
};

export function useDashboardEdit() {
  return useContext(DashboardEditContext) ?? defaultEditContext;
}
